import { textGray } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import React from 'react';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import useModal from '../../../hooks/useModal';
import { opacity } from '../../../styles/hover';

import PopupMenu, { PopupMenuItem } from '../../../components/PopupMenu';
import PopupMenuRow from '../../../components/PopupMenuRow';
import { useIntl } from 'react-intl';
import { PlayListSort } from '@elsevier-health/osmosis-shared/types/playlist';
import { playListsSortMap } from '../../../lib/shared/playlist/utils';

type Props = {
  setSortValue: (sortValue: string) => void;
  sortValue?: string;
};

/**
 * PlaylistSortByButton.
 */
const PlaylistSortByButton = (props: Props) => {
  const { setSortValue, sortValue = '' } = props;
  const { formatMessage } = useIntl();
  const [sortByButtonText, setSortByButtonText] = React.useState(
    playListsSortMap[sortValue] ||
      formatMessage({
        id: 'playlist.sortby.default',
        defaultMessage: 'Sort By',
      }),
  );

  const intl = useIntl();

  const {
    isVisible: sortByMenuVisible,
    hide,
    toggle: toggleSortByMenu,
  } = useModal();

  const handleToggleSortBy = (filter: string) => {
    setSortValue(filter);
    hide();
  };

  function handleRecentlyEditedClick() {
    handleToggleSortBy(PlayListSort.RECENTLY_EDITED);
    setSortByButtonText(playListsSortMap[PlayListSort.RECENTLY_EDITED]);
  }

  function handleRecentlyCreatedClick() {
    handleToggleSortBy(PlayListSort.RECENTLY_CREATED);
    setSortByButtonText(playListsSortMap[PlayListSort.RECENTLY_CREATED]);
  }

  function handleAlphabeticalClick() {
    handleToggleSortBy(PlayListSort.ALPHABETICAL_A_Z);
    setSortByButtonText(playListsSortMap[PlayListSort.ALPHABETICAL_A_Z]);
  }

  const sortByOptions = [
    {
      id: `sortby-${PlayListSort.RECENTLY_EDITED}`,
      text: intl.formatMessage({
        id: `playlist.sortby.button.${PlayListSort.RECENTLY_EDITED}`,
        defaultMessage: 'Recently edited',
      }),
      onClick: handleRecentlyEditedClick,
    },
    {
      id: `sortby-${PlayListSort.RECENTLY_CREATED}`,
      text: intl.formatMessage({
        id: `playlist.sortby.button.${PlayListSort.RECENTLY_CREATED}`,
        defaultMessage: 'Recently created',
      }),
      onClick: handleRecentlyCreatedClick,
    },
    {
      id: `sortby-${PlayListSort.ALPHABETICAL_A_Z}`,
      text: intl.formatMessage({
        id: `playlist.sortby.button.${PlayListSort.ALPHABETICAL_A_Z}`,
        defaultMessage: 'Alphabetical a-z',
      }),
      onClick: handleAlphabeticalClick,
    },
  ].filter(Boolean) as PopupMenuItem[];

  const buttonText = formatMessage(
    {
      id: 'playlist-sortBy-message',
      defaultMessage: '{sortByButtonText}',
    },
    { sortByButtonText },
  );

  return (
    <Box>
      <PopupMenu
        keyExtractor={(item) => item.id}
        isVisible={sortByMenuVisible}
        renderItem={(item) => <PopupMenuRow item={item} />}
        items={sortByOptions}
        hide={hide}
        hideEvent="scroll"
        showHeader={false}
        containerClasses="pt-[16px] m-0"
      >
        <Box
          style={{
            ...opacity(0.75),
          }}
        >
          <Button
            variant="outline"
            onClick={toggleSortByMenu}
            data-testid="sortBy-playlist-btn"
            endIcon={
              sortByMenuVisible ? (
                <Icon icon={faChevronUp} />
              ) : (
                <Icon icon={faChevronDown} />
              )
            }
            className={`mr-2 ${textGray} flex w-[100%] justify-between`}
            aria-haspopup="true"
            aria-expanded={sortByMenuVisible}
            aria-label={buttonText}
          >
            {buttonText}
          </Button>
        </Box>
      </PopupMenu>
    </Box>
  );
};

export default PlaylistSortByButton;
